'use client';

import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { TwitterEmbed } from '@/components/ui/SocialEmbed';
import { TagMention } from '@/components/ui/TagMention';
import { processMarkdownForSizing, parseTextWithMentions } from '@/utils/markdown-utils';

interface EntityGlossaryProps {
  content: string;
  collapsible?: boolean;
  initiallyExpanded?: boolean;
  className?: string;
}

export default function EntityGlossary({
  content,
  collapsible = false,
  initiallyExpanded = true,
  className = ''
}: EntityGlossaryProps) {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  
  // Check if content is long enough to require truncation
  const wordCount = content.split(/\s+/).length;
  const shouldTruncate = wordCount > 150;
  const [showFullContent, setShowFullContent] = useState(!shouldTruncate);

  // Process markdown content for image sizing (memoized)
  const processedContent = useMemo(
    () => processMarkdownForSizing(content),
    [content]
  );

  // Memoize markdown components to prevent re-renders
  const markdownComponents = useMemo(() => ({
    // Override paragraph to prevent nesting issues
    p: ({ children, node, ...props }: any) => {
      // Check if this paragraph contains only an image (which might be a TwitterEmbed)
      if (node && node.children && node.children.length === 1 && node.children[0].tagName === 'img') {
        return <>{children}</>;
      }

      // Detect block-level elements that can't be nested in <p> tags
      const childArray = React.Children.toArray(children);

      // Recursive function to check if any child is a block-level element
      const hasBlockLevelChild = (children: any): boolean => {
        const childArray = React.Children.toArray(children);

        return childArray.some((child) => {
          if (React.isValidElement(child)) {
            const childType = child.type;

            // String type names (HTML elements)
            if (typeof childType === 'string') {
              const blockElements = ['div', 'blockquote', 'figure', 'img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'table', 'pre', 'hr', 'p'];
              if (blockElements.includes(childType)) {
                return true;
              }
            }

            // Check for Suspense boundaries or TwitterEmbed
            if (childType && (childType.toString().includes('Suspense') || childType.toString().includes('Loadable'))) {
              return true;
            }

            if (childType === TwitterEmbed ||
              (childType && typeof childType === 'function' && childType.name === 'TwitterEmbed') ||
              (child.props && child.props.tweetUrl)) {
              return true;
            }

            // Recursively check children
            if (child.props && child.props.children) {
              return hasBlockLevelChild(child.props.children);
            }
          }
          return false;
        });
      };

      // Parse tag mentions in children
      const childrenWithMentions = React.Children.map(children, child => {
        if (typeof child === 'string') {
          const segments = parseTextWithMentions(child);
          return segments.map((segment, index) => {
            if (segment.type === 'mention') {
              return <TagMention key={`mention-${index}`} slug={segment.value} />;
            }
            return segment.value;
          });
        }
        return child;
      });

      // If contains block-level elements, use div wrapper
      if (hasBlockLevelChild(childArray)) {
        return <div className="my-4">{childrenWithMentions}</div>;
      }

      // Otherwise, render as normal paragraph
      return <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4" {...props}>{childrenWithMentions}</p>;
    },
    a: ({ href, children, node, ...props }: any) => (
      <a
        href={href}
        className="text-scout-gold hover:text-scout-gold/80 underline decoration-scout-gold/40 hover:decoration-scout-gold underline-offset-2 transition-colors"
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
      </a>
    ),
    h1: ({ node, ...props }: any) => (
      <h1 {...props} className="text-3xl font-bold text-gray-900 dark:text-white mt-6 mb-4" />
    ),
    h2: ({ node, ...props }: any) => (
      <h2 {...props} className="text-2xl font-bold text-gray-900 dark:text-white mt-5 mb-3" />
    ),
    h3: ({ node, ...props }: any) => (
      <h3 {...props} className="text-xl font-bold text-gray-900 dark:text-white mt-4 mb-2" />
    ),
    ul: ({ children }: any) => (
      <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-2">
        {children}
      </ul>
    ),
    ol: ({ children }: any) => (
      <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-2">
        {children}
      </ol>
    ),
    li: ({ children }: any) => {
      // Parse tag mentions in list items
      const childrenWithMentions = React.Children.map(children, child => {
        if (typeof child === 'string') {
          const segments = parseTextWithMentions(child);
          return segments.map((segment, index) => {
            if (segment.type === 'mention') {
              return <TagMention key={`mention-${index}`} slug={segment.value} />;
            }
            return segment.value;
          });
        }
        return child;
      });
      return <li className="text-gray-700 dark:text-gray-300 leading-relaxed">{childrenWithMentions}</li>;
    },
    blockquote: ({ children, node, ...props }: any) => (
      <blockquote className="border-l-4 border-scout-gold/60 pl-4 italic text-gray-700 dark:text-gray-300 my-4" {...props}>
        {children}
      </blockquote>
    ),
    code: ({ node, inline, ...props }: any) =>
      inline ? (
        <code {...props} className="bg-gray-100 dark:bg-brand-dark-lighter text-pink-600 dark:text-pink-400 px-1.5 py-0.5 rounded text-sm font-mono" />
      ) : (
        <code {...props} className="block bg-gray-100 dark:bg-brand-dark-lighter text-gray-900 dark:text-white p-4 rounded-lg overflow-x-auto font-mono text-sm my-4" />
      ),
    img: ({ src, alt }: { src?: string; alt?: string }) => {
      // Extract width from src if present
      let width: string | undefined = undefined;
      let cleanSrc = src || '';

      // Check for {width=X} syntax
      const encodedMatch = cleanSrc.match(/%7Bwidth=(\d+(?:%|px)?)%7D$/i);
      if (encodedMatch) {
        width = encodedMatch[1];
        cleanSrc = cleanSrc.replace(/%7Bwidth=\d+(?:%|px)?%7D$/i, '');
      } else {
        const plainMatch = cleanSrc.match(/\{width=(\d+(?:%|px)?)\}$/);
        if (plainMatch) {
          width = plainMatch[1];
          cleanSrc = cleanSrc.replace(/\{width=\d+(?:%|px)?\}$/, '');
        }
      }

      // Check for Twitter URLs
      const twitterMatch = cleanSrc.match(/(?:twitter\.com|x\.com)\/[\w]+\/status\/(\d+)/);
      if (twitterMatch) {
        const normalizedUrl = cleanSrc.replace(/x\.com/, 'twitter.com');
        return <TwitterEmbed key={normalizedUrl} tweetUrl={normalizedUrl} caption={alt} />;
      }

      return (
        <img
          src={cleanSrc}
          alt={alt || ''}
          className="rounded-lg h-auto block mx-auto my-4"
          style={width ? { maxWidth: width.endsWith('%') || width.endsWith('px') ? width : `${width}px` } : { maxWidth: '100%' }}
        />
      );
    },
  }), []);

  if (!content || content.trim() === '') {
    return null;
  }

  const MarkdownContent = () => (
    <div className={`prose prose-slate dark:prose-invert max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        skipHtml={false}
        components={markdownComponents}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );

  if (!collapsible) {
    return (
      <div className={className}>
        <div
          className={`relative transition-all duration-500 ease-in-out overflow-hidden ${!showFullContent ? 'max-h-[600px]' : 'max-h-none'
            }`}
        >
          <MarkdownContent />

          {/* Gradient Overlay when collapsed */}
          {!showFullContent && (
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white dark:from-neutral-900 to-transparent pointer-events-none" />
          )}
        </div>

        {shouldTruncate && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={() => setShowFullContent(!showFullContent)}
              className="group flex items-center gap-2 px-6 py-2.5 bg-gray-100 dark:bg-neutral-800 hover:bg-gray-200 dark:hover:bg-neutral-700 text-gray-700 dark:text-gray-200 font-medium rounded-full transition-all"
            >
              {showFullContent ? (
                <>
                  Show Less
                  <ChevronUp className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform" />
                </>
              ) : (
                <>
                  Read Full Description
                  <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left px-4 py-3 bg-gray-50 dark:bg-brand-dark-lighter rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors mb-3"
      >
        <span className="font-semibold text-gray-900 dark:text-white">
          About
        </span>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {isExpanded && <MarkdownContent />}
    </div>
  );
}

